package fr.frux.feature.harvestcreate

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.CalendarMonth
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Info
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material.icons.outlined.Remove
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SelectableDates
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxCard
import fr.frux.core.designsystem.components.FruxTextField
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.interaction.clearFocusOnTap
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.designsystem.theme.label
import fr.frux.feature.harvestcreate.resources.Res
import fr.frux.feature.harvestcreate.resources.harvestcreate_close
import fr.frux.feature.harvestcreate.resources.harvestcreate_date_picker_cancel
import fr.frux.feature.harvestcreate.resources.harvestcreate_date_picker_confirm
import fr.frux.feature.harvestcreate.resources.harvestcreate_description_label
import fr.frux.feature.harvestcreate.resources.harvestcreate_description_placeholder
import fr.frux.feature.harvestcreate.resources.harvestcreate_error_past_slot
import fr.frux.feature.harvestcreate.resources.harvestcreate_foraging_rule_notice
import fr.frux.feature.harvestcreate.resources.harvestcreate_hour_label
import fr.frux.feature.harvestcreate.resources.harvestcreate_other_date
import fr.frux.feature.harvestcreate.resources.harvestcreate_participants_count
import fr.frux.feature.harvestcreate.resources.harvestcreate_participants_decrement
import fr.frux.feature.harvestcreate.resources.harvestcreate_participants_increment
import fr.frux.feature.harvestcreate.resources.harvestcreate_participants_unlimited
import fr.frux.feature.harvestcreate.resources.harvestcreate_section_participants
import fr.frux.feature.harvestcreate.resources.harvestcreate_section_when
import fr.frux.feature.harvestcreate.resources.harvestcreate_submit
import fr.frux.feature.harvestcreate.resources.harvestcreate_title
import fr.frux.feature.harvestcreate.resources.harvestcreate_tree_loading
import fr.frux.feature.harvestcreate.resources.harvestcreate_tree_unavailable
import fr.frux.shared.content.DescriptionLength
import fr.frux.shared.domain.model.Harvest
import fr.frux.shared.domain.model.HarvestTarget
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.TreeStatus
import fr.frux.shared.presentation.toFruxStatus
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * Formulaire « Lancer une récolte » (#167), poussé depuis la fiche d'arbre ou la carte.
 *
 * Icône de fermeture (croix) plutôt que flèche retour : c'est un formulaire modal, pas une
 * navigation en profondeur.
 *
 * @param target Arbre ou lieu visé par la récolte.
 * @param onClose Fermeture du formulaire (croix ou après publication réussie ailleurs).
 * @param onHarvestCreated Publication réussie : l'hôte navigue vers la fiche récolte en
 * remplaçant ce formulaire dans la pile (le retour depuis la fiche ne doit pas le rouvrir).
 */
@Composable
fun CreateHarvestScreen(
    target: HarvestTarget,
    onClose: () -> Unit,
    onHarvestCreated: (Harvest) -> Unit = {},
    modifier: Modifier = Modifier,
    viewModel: CreateHarvestViewModel = rememberCreateHarvestViewModel(target),
) {
    val state by viewModel.state.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }

    LaunchedEffect(Unit) {
        viewModel.events.collect { event ->
            when (event) {
                is CreateHarvestEvent.Created -> onHarvestCreated(event.harvest)
            }
        }
    }

    SnackbarMessageEffect(
        trigger = state.actionError,
        message = state.actionError?.message(),
        snackbarHostState = snackbarHostState,
    ) { viewModel.consumeActionError() }

    Scaffold(
        // Tap hors du champ actif -> ferme le clavier (#126), sans consommer le geste.
        modifier = modifier.fillMaxSize().clearFocusOnTap(),
        topBar = {
            FruxTopBar(
                title = stringResource(Res.string.harvestcreate_title),
                onBack = onClose,
                navigationIcon = Icons.Outlined.Close,
                navigationContentDescription = stringResource(Res.string.harvestcreate_close),
            )
        },
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        Column(
            modifier =
                Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.md),
        ) {
            if (state.target is HarvestTarget.Site) {
                SiteReminderCard(site = state.site, isLoading = state.isTargetLoading)
                if (state.canHostAsOwner) {
                    HostedByOwnerSwitch(
                        checked = state.hostedByOwner,
                        onCheckedChange = viewModel::onHostedByOwnerChanged,
                    )
                }
            } else {
                TreeReminderCard(tree = state.tree, isLoading = state.isTargetLoading)
            }

            DateTimeField(
                dayOptions = viewModel.dayOptions,
                selectedDate = state.selectedDate,
                selectedHour = state.selectedHour,
                today = viewModel.today,
                maxDate = viewModel.maxDate,
                onDaySelected = viewModel::onDaySelected,
                onHourSelected = viewModel::onHourSelected,
                isPastSlot = viewModel::isPastSlot,
                isDayFullyPast = viewModel::isDayFullyPast,
                isDateSelectable = viewModel::isDateSelectable,
            )

            FruxTextField(
                value = state.description,
                onValueChange = viewModel::onDescriptionChange,
                label = stringResource(Res.string.harvestcreate_description_label),
                placeholder = stringResource(Res.string.harvestcreate_description_placeholder),
                singleLine = false,
                maxLength = DescriptionLength.MAX,
                modifier = Modifier.fillMaxWidth(),
            )

            if (state.hostedByOwner) {
                AccessHintChips(applied = state.appliedHints, onHintClicked = viewModel::onAccessHintClicked)
            }

            MaxParticipantsField(
                maxParticipants = state.maxParticipants,
                onIncrement = viewModel::onMaxParticipantsIncrement,
                onDecrement = viewModel::onMaxParticipantsDecrement,
            )

            ForagingRuleNotice()

            FruxButton(
                text = stringResource(Res.string.harvestcreate_submit),
                onClick = viewModel::submit,
                loading = state.isSubmitting,
                modifier = Modifier.fillMaxWidth().padding(top = FruxSpacing.sm, bottom = FruxSpacing.lg),
            )
        }
    }
}

@Composable
private fun TreeReminderCard(
    tree: Tree?,
    isLoading: Boolean,
) {
    FruxCard(modifier = Modifier.fillMaxWidth()) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                imageVector = Icons.Outlined.Park,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
            )
            Spacer(Modifier.width(FruxSpacing.sm))
            Text(
                text =
                    when {
                        tree != null -> "${tree.species.commonNameFr} · ${tree.status.toFruxStatus().label}"
                        isLoading -> stringResource(Res.string.harvestcreate_tree_loading)
                        else -> stringResource(Res.string.harvestcreate_tree_unavailable)
                    },
                style = MaterialTheme.typography.bodyMedium,
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalTime::class)
@Composable
private fun DateTimeField(
    dayOptions: List<LocalDate>,
    selectedDate: LocalDate,
    selectedHour: Int,
    today: LocalDate,
    maxDate: LocalDate,
    onDaySelected: (LocalDate) -> Unit,
    onHourSelected: (Int) -> Unit,
    isPastSlot: (LocalDate, Int) -> Boolean,
    isDayFullyPast: (LocalDate) -> Boolean,
    isDateSelectable: (LocalDate) -> Boolean,
) {
    var isDatePickerOpen by remember { mutableStateOf(false) }
    val isOtherDateSelected = selectedDate !in dayOptions

    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs)) {
        SectionLabel(stringResource(Res.string.harvestcreate_section_when))
        FlowRow(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.xs)) {
            dayOptions.forEach { date ->
                FilterChip(
                    selected = date == selectedDate,
                    onClick = { onDaySelected(date) },
                    // Aujourd'hui après le dernier créneau : plus rien à y choisir (#310).
                    enabled = !isDayFullyPast(date),
                    label = { Text(date.shortLabel()) },
                )
            }
            FilterChip(
                selected = isOtherDateSelected,
                onClick = { isDatePickerOpen = true },
                leadingIcon = { Icon(imageVector = Icons.Outlined.CalendarMonth, contentDescription = null) },
                label = {
                    Text(
                        if (isOtherDateSelected) {
                            selectedDate.longLabel()
                        } else {
                            stringResource(Res.string.harvestcreate_other_date)
                        },
                    )
                },
            )
        }
        Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.xs)) {
            HOUR_OPTIONS.forEach { hour ->
                FilterChip(
                    selected = hour == selectedHour,
                    onClick = { onHourSelected(hour) },
                    // Créneau déjà passé pour le jour choisi : le serveur le refuserait (#310).
                    enabled = !isPastSlot(selectedDate, hour),
                    label = { Text(stringResource(Res.string.harvestcreate_hour_label, hour)) },
                )
            }
        }
    }

    if (isDatePickerOpen) {
        HarvestDatePickerDialog(
            selectedDate = selectedDate,
            today = today,
            maxDate = maxDate,
            isDateSelectable = isDateSelectable,
            onConfirm = { date ->
                onDaySelected(date)
                isDatePickerOpen = false
            },
            onDismiss = { isDatePickerOpen = false },
        )
    }
}

/**
 * Date picker M3 pour la planification lointaine (#339, jusqu'à J+28) : les 4 pastilles jour ne
 * couvrent que le court terme, le serveur accepte un an mais la règle produit borne à 4 semaines.
 * M3 travaille en millis UTC minuit, d'où la conversion aller-retour avec [LocalDate].
 */
@OptIn(ExperimentalMaterial3Api::class, ExperimentalTime::class)
@Composable
private fun HarvestDatePickerDialog(
    selectedDate: LocalDate,
    today: LocalDate,
    maxDate: LocalDate,
    isDateSelectable: (LocalDate) -> Boolean,
    onConfirm: (LocalDate) -> Unit,
    onDismiss: () -> Unit,
) {
    val datePickerState =
        rememberDatePickerState(
            initialSelectedDateMillis = selectedDate.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds(),
            selectableDates =
                remember(isDateSelectable, today, maxDate) {
                    object : SelectableDates {
                        override fun isSelectableDate(utcTimeMillis: Long): Boolean =
                            isDateSelectable(utcTimeMillis.toUtcLocalDate())

                        override fun isSelectableYear(year: Int): Boolean = year in today.year..maxDate.year
                    }
                },
        )

    DatePickerDialog(
        onDismissRequest = onDismiss,
        confirmButton = {
            TextButton(
                onClick = {
                    datePickerState.selectedDateMillis?.let { millis -> onConfirm(millis.toUtcLocalDate()) }
                },
            ) {
                Text(stringResource(Res.string.harvestcreate_date_picker_confirm))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(stringResource(Res.string.harvestcreate_date_picker_cancel))
            }
        },
    ) {
        // Sans titre : l'en-tête de date suffit, le titre M3 dépasse du cadre du dialog sur js.
        DatePicker(state = datePickerState, title = null)
    }
}

@OptIn(ExperimentalTime::class)
private fun Long.toUtcLocalDate(): LocalDate = Instant.fromEpochMilliseconds(this).toLocalDateTime(TimeZone.UTC).date

@Composable
private fun MaxParticipantsField(
    maxParticipants: Int?,
    onIncrement: () -> Unit,
    onDecrement: () -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs)) {
        SectionLabel(stringResource(Res.string.harvestcreate_section_participants))
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.md),
        ) {
            IconButton(onClick = onDecrement) {
                Icon(
                    imageVector = Icons.Outlined.Remove,
                    contentDescription = stringResource(Res.string.harvestcreate_participants_decrement),
                )
            }
            Text(
                text = maxParticipantsLabel(maxParticipants),
                style = MaterialTheme.typography.bodyLarge,
            )
            IconButton(onClick = onIncrement) {
                Icon(
                    imageVector = Icons.Outlined.Add,
                    contentDescription = stringResource(Res.string.harvestcreate_participants_increment),
                )
            }
        }
    }
}

/** Même mapping que HarvestDetailScreen.cueilleursLabel — redupliqué, pas de dépendance horizontale. */
@Composable
private fun maxParticipantsLabel(maxParticipants: Int?): String =
    if (maxParticipants == null) {
        stringResource(Res.string.harvestcreate_participants_unlimited)
    } else {
        stringResource(Res.string.harvestcreate_participants_count, maxParticipants)
    }

/**
 * Encart obligatoire (#167) : la récolte publiée est visible de tous les autres cueilleurs, et
 * le glanage ne se pratique que sur le domaine public. Ne pas retirer — c'est la règle qui protège
 * l'app d'un signalement de cueillette illégale.
 */
@Composable
private fun ForagingRuleNotice() {
    // Pas FruxCard : sa Surface impose la couleur `surface` (#164 le montre déjà), or cet encart
    // doit être en `primaryContainer` — obligation de la spec, à ne pas fondre dans le style card.
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.large,
        color = MaterialTheme.colorScheme.primaryContainer,
    ) {
        Row(modifier = Modifier.padding(FruxSpacing.md)) {
            Icon(
                imageVector = Icons.Outlined.Info,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onPrimaryContainer,
            )
            Spacer(Modifier.width(FruxSpacing.sm))
            Text(
                text = stringResource(Res.string.harvestcreate_foraging_rule_notice),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onPrimaryContainer,
            )
        }
    }
}

@Composable
private fun SectionLabel(text: String) {
    Text(text = text, style = MaterialTheme.typography.titleSmall)
}

private val SHORT_DAY_NAMES = listOf("lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim.")

private val SHORT_MONTH_NAMES =
    listOf(
        "janv.",
        "févr.",
        "mars",
        "avr.",
        "mai",
        "juin",
        "juil.",
        "août",
        "sept.",
        "oct.",
        "nov.",
        "déc.",
    )

/** Libellé court, ex. « sam. 15 » — jamais « Aujourd'hui »/« Demain » (spec #167, pas de table de mots). */
private fun LocalDate.shortLabel(): String = "${SHORT_DAY_NAMES[dayOfWeek.ordinal]} $day"

/** Libellé d'une date choisie hors pastilles (#339), ex. « sam. 3 oct. ». */
private fun LocalDate.longLabel(): String =
    "${SHORT_DAY_NAMES[dayOfWeek.ordinal]} $day ${SHORT_MONTH_NAMES[monthNumber - 1]}"

/** Libellé du refus de publication (#176). */
@Composable
private fun CreateHarvestError.message(): String =
    when (this) {
        CreateHarvestError.PastSlot -> stringResource(Res.string.harvestcreate_error_past_slot)
        is CreateHarvestError.Failure -> error.userMessage()
    }
